import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import {
  isBridgeMessageTargetForFrontend,
  isUserAvailability,
} from "@bezoned/desktop-bridge/bridge-predicates.js";
import {
  DESKTOP_PRESENCE_MESSAGE_TYPES,
  isDesktopPresenceBridgeMessage,
} from "@bezoned/desktop-bridge/desktop-presence-messaging.js";
import type { DesktopPresenceChangeRequestPayload } from "@bezoned/desktop-bridge/desktop-presence-messaging.js";
import { DESKTOP_SYNC_MESSAGE_TYPES } from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type { DesktopSyncPresenceSection } from "@bezoned/desktop-bridge/desktop-sync-messaging.js";
import type { JsonMessagePayload } from "@bezoned/desktop-bridge/events.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useMyPresence } from "../presence/use-my-presence.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";

const getDesktopPresenceChangeRequest = ({
  payload,
  bridgeRoom,
}: {
  payload: JsonMessagePayload;
  bridgeRoom: string;
}): DesktopPresenceChangeRequestPayload | null => {
  if (payload.room !== bridgeRoom) {
    return null;
  }

  if (!isDesktopPresenceBridgeMessage(payload.data)) {
    return null;
  }

  if (payload.data.type !== DESKTOP_PRESENCE_MESSAGE_TYPES.CHANGE_REQUEST) {
    return null;
  }

  if (
    payload.data.source !== BRIDGE_MESSAGE_SOURCES.DESKTOP ||
    !isBridgeMessageTargetForFrontend(payload.data.target)
  ) {
    return null;
  }

  return payload.data.payload;
};

const toPresenceSection = ({
  availability,
  isOutOfOffice,
  statusMessage,
}: {
  availability: UserAvailability;
  isOutOfOffice: boolean;
  statusMessage: string | null;
}): DesktopSyncPresenceSection => ({
  availability,
  outOfOffice: isOutOfOffice,
  statusMessage,
});

export const DesktopBridgePresenceSyncListener = () => {
  const {
    availability,
    isOutOfOffice,
    statusMessage,
    updateAvailability,
    updateStatusMessage,
  } = useMyPresence();
  const {
    status: bridgeStatus,
    instanceId,
    sendJson,
    onJsonMessage,
  } = useDesktopBridge();
  const bridgeRoom = useDesktopBridgeRoom();

  const presenceSection = useMemo(
    () =>
      availability === null
        ? null
        : toPresenceSection({
            availability,
            isOutOfOffice,
            statusMessage,
          }),
    [availability, isOutOfOffice, statusMessage]
  );
  const presenceSectionRef = useRef(presenceSection);
  const lastPresenceSignatureRef = useRef<string>("");
  presenceSectionRef.current = presenceSection;

  const sendPresenceUpdate = useCallback(
    (nextPresence: DesktopSyncPresenceSection | null) => {
      if (nextPresence === null) {
        lastPresenceSignatureRef.current = "";
        return;
      }
      if (!(bridgeStatus === "ready" && instanceId)) {
        return;
      }

      const nextSignature = JSON.stringify(nextPresence);
      if (nextSignature === lastPresenceSignatureRef.current) {
        return;
      }
      lastPresenceSignatureRef.current = nextSignature;

      sendJson(
        bridgeRoom,
        createBridgeEnvelope({
          instanceId,
          payload: {
            sections: {
              presence: nextPresence,
            },
          },
          source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
          target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
          type: DESKTOP_SYNC_MESSAGE_TYPES.PRESENCE_UPDATE,
        })
      );
    },
    [bridgeRoom, bridgeStatus, instanceId, sendJson]
  );

  useEffect(() => {
    if (bridgeStatus === "ready") {
      sendPresenceUpdate(presenceSection);
      return;
    }

    lastPresenceSignatureRef.current = "";
  }, [bridgeStatus, presenceSection, sendPresenceUpdate]);

  const handleDesktopPresenceRequest = useCallback(
    (payload: JsonMessagePayload) => {
      const requestPayload = getDesktopPresenceChangeRequest({
        bridgeRoom,
        payload,
      });
      if (!requestPayload) {
        return;
      }

      const {
        availability: nextAvailability,
        statusMessage: nextStatusMessage,
      } = requestPayload;

      let optimisticPresence = presenceSectionRef.current;
      const mutations: Promise<void>[] = [];

      if (nextAvailability !== undefined) {
        if (isUserAvailability(nextAvailability)) {
          if (optimisticPresence) {
            optimisticPresence = {
              ...optimisticPresence,
              availability: nextAvailability,
            };
          }
          mutations.push(updateAvailability(nextAvailability));
        } else {
          recordErrorInSentry(
            new Error(
              `[DesktopBridgePresenceSyncListener] Invalid availability in desktop bridge request: ${nextAvailability}`
            )
          );
        }
      }

      if (nextStatusMessage !== undefined) {
        if (optimisticPresence) {
          optimisticPresence = {
            ...optimisticPresence,
            statusMessage: nextStatusMessage,
          };
        }
        mutations.push(updateStatusMessage(nextStatusMessage));
      }

      if (mutations.length === 0) {
        sendPresenceUpdate(presenceSectionRef.current);
        return;
      }

      sendPresenceUpdate(optimisticPresence);

      Promise.allSettled(mutations).then((results) => {
        const failedResults = results.filter(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected"
        );

        if (failedResults.length > 0) {
          for (const failedResult of failedResults) {
            recordErrorInSentry(failedResult.reason);
          }
        }

        sendPresenceUpdate(presenceSectionRef.current);
      });
    },
    [bridgeRoom, sendPresenceUpdate, updateAvailability, updateStatusMessage]
  );

  useEffect(
    () => onJsonMessage(handleDesktopPresenceRequest),
    [handleDesktopPresenceRequest, onJsonMessage]
  );

  return null;
};
